import React, { useState } from "react";
import { FiWifi, FiRadio, FiBattery, FiAlertTriangle } from "react-icons/fi";
import { signalQuality, battColor, nodeStatusColor, MiniBar, NodeBadge } from "./irrigationHelpers";

const IrrLoraPanel = ({ d }) => {
  const { loraNetwork } = d;
  const [selected, setSelected] = useState(null);
  const [sortBy, setSortBy] = useState("id");

  const online  = loraNetwork.nodes.filter(n => n.status === "Online").length;
  const weak    = loraNetwork.nodes.filter(n => n.status === "Weak").length;
  const offline = loraNetwork.nodes.filter(n => n.status === "Offline").length;
  const lowBatt = loraNetwork.nodes.filter(n => n.status === "Low Batt" || n.battery < 30).length;

  const sorted = [...loraNetwork.nodes].sort((a, b) => {
    if (sortBy === "rssi")    return (a.rssi ?? -999) - (b.rssi ?? -999);
    if (sortBy === "battery") return a.battery - b.battery;
    if (sortBy === "pktloss") return b.packetLoss - a.packetLoss;
    return a.id.localeCompare(b.id);
  });

  const sel = selected ? loraNetwork.nodes.find(n => n.id === selected) : null;

  return (
    <div className="irr-lora-panel">

      {/* ── Gateway card ── */}
      <div className="irr-gw-card">
        <div className="irr-gw-left">
          <div className="irr-gw-icon"><FiRadio size={22} color="#667eea" /></div>
          <div>
            <div className="irr-gw-name">{loraNetwork.gateway.name}</div>
            <div className="irr-gw-id">{loraNetwork.gateway.id} · IP: {loraNetwork.gateway.ip} · FW {loraNetwork.gateway.firmware}</div>
          </div>
        </div>
        <div className="irr-gw-stats">
          <div className="irr-gw-stat"><span className="irr-badge" style={{ background: "#d1fae5", color: "#10b981", fontSize: 13 }}>{loraNetwork.gateway.status}</span></div>
          <div className="irr-gw-stat"><span>RSSI: {loraNetwork.gateway.rssi} dBm</span></div>
          <div className="irr-gw-stat"><span>SNR: {loraNetwork.gateway.snr} dB</span></div>
          <div className="irr-gw-stat"><span>Uptime: {loraNetwork.gateway.uptime}</span></div>
        </div>
      </div>

      {/* ── Network summary ── */}
      <div className="irr-lora-summary">
        {[
          { label: "Total Nodes",    value: loraNetwork.nodes.length, color: "#667eea" },
          { label: "Online",         value: online,  color: "#10b981" },
          { label: "Weak Signal",    value: weak,    color: "#f97316" },
          { label: "Offline",        value: offline, color: "#ef4444" },
          { label: "Low Battery",    value: lowBatt, color: "#f59e0b" },
        ].map(s => (
          <div key={s.label} className="irr-lora-sum-card" style={{ borderTopColor: s.color }}>
            <div className="irr-lora-sum-val" style={{ color: s.color }}>{s.value}</div>
            <div className="irr-lora-sum-lbl">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="irr-lora-body">

        {/* ── Node table ── */}
        <div className="irr-node-table-wrap">
          <div className="irr-sort-row">
            <span className="irr-sort-label">Sort by:</span>
            {[["id","Node ID"],["rssi","RSSI"],["battery","Battery"],["pktloss","Packet Loss"]].map(([k, lbl]) => (
              <button key={k} className={`irr-sort-btn ${sortBy === k ? "irr-sort-active" : ""}`} onClick={() => setSortBy(k)}>{lbl}</button>
            ))}
          </div>
          <table className="irr-table">
            <thead>
              <tr>
                <th>Node</th><th>Zone</th><th>Status</th><th>RSSI</th><th>SNR</th><th>Battery</th><th>Pkt Loss</th><th>Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(n => {
                const sq = signalQuality(n.rssi);
                return (
                  <tr key={n.id} className={`${n.id === selected ? "irr-tr-sel" : ""} ${n.status !== "Online" ? "irr-tr-warn" : ""}`}
                    onClick={() => setSelected(n.id === selected ? null : n.id)} style={{ cursor: "pointer" }}>
                    <td style={{ fontWeight: 600 }}>{n.id}</td>
                    <td>{n.zone}</td>
                    <td><NodeBadge status={n.status} /></td>
                    <td style={{ color: sq.color }}>{n.rssi != null ? `${n.rssi} dBm` : "—"}</td>
                    <td>{n.snr != null ? `${n.snr} dB` : "—"}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <MiniBar value={n.battery} max={100} color={battColor(n.battery)} height={6} />
                        <span style={{ color: battColor(n.battery), minWidth: 32 }}>{n.battery}%</span>
                      </div>
                    </td>
                    <td style={{ color: n.packetLoss > 3 ? "#ef4444" : n.packetLoss > 1 ? "#f59e0b" : "#10b981" }}>{n.packetLoss}%</td>
                    <td style={{ color: "#6b7280", fontSize: 12 }}>{n.lastSeen}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Node detail ── */}
        {sel && (
          <div className="irr-node-detail">
            <div className="irr-nd-title">{sel.id} Details</div>
            <div className="irr-nd-zone">Zone: {sel.zone}</div>
            <NodeBadge status={sel.status} />
            <div className="irr-nd-stats">
              {[
                { label: "RSSI",        value: sel.rssi != null ? `${sel.rssi} dBm` : "N/A",  color: signalQuality(sel.rssi).color },
                { label: "SNR",         value: sel.snr  != null ? `${sel.snr} dB`   : "N/A",  color: "#667eea" },
                { label: "Packet Loss", value: `${sel.packetLoss}%`,  color: sel.packetLoss > 3 ? "#ef4444" : "#10b981" },
                { label: "Battery",     value: `${sel.battery}%`,     color: battColor(sel.battery) },
                { label: "Firmware",    value: sel.firmware,           color: "#6b7280" },
                { label: "Last Seen",   value: sel.lastSeen,           color: "#374151" },
              ].map(s => (
                <div key={s.label} className="irr-nd-stat">
                  <div className="irr-nd-val" style={{ color: s.color }}>{s.value}</div>
                  <div className="irr-nd-lbl">{s.label}</div>
                </div>
              ))}
            </div>
            {(sel.status === "Weak" || sel.status === "Offline") && (
              <div className="irr-nd-warn">
                <FiAlertTriangle color="#f59e0b" size={14} />
                {sel.status === "Offline" ? "Node offline – check power and antenna." : "Weak signal – consider repositioning node or adding repeater."}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IrrLoraPanel;
